import React from 'react';
import * as DataProviders from '../../types/data-providers';
import { useData, dataFromDataset } from './DataProviderCache';
import * as ApiRequests from '../../api/requests';
import { useAuthHelper } from '../../components/AuthContextProvider';

type ContextState = DataProviders.Data<{
    alarmId: string;
    trigger: string;
    severity: string;
    channelName: string | null;
    status: string;
    value: string;
    threshold: string;
    dateTime: string;
}>;

const Context = React.createContext<ContextState | null>(null);

function useAlarmStatusData(): ContextState {
    const contextState = React.useContext(Context);
    if (contextState === null) {
        throw new Error('useAlarmStatusData must be used within a AlarmStatusProvider');
    }
    return contextState;
}

const AlarmStatusDataProvider: React.FC<{
    alarmId: string;
    deviceId: string;
}> = (props) => {
    const authHelper = useAuthHelper();

    const { dataset } = useData({
        cacheKey: 'site-alarm-timeline-data',
        id: `${props.alarmId}-${props.deviceId}`,
        dataFn: React.useCallback(async () => {
            const result = await ApiRequests.getAlarmStatus(
                {
                    alarmId: props.alarmId,
                    deviceId: props.deviceId,
                },
                { authHelper }
            );

            if (result.ok) {
                return result.response.data.alarmStatus;
            }

            throw new Error(ApiRequests.extractResponseNotOkMessage(result.response));
        }, [authHelper, props.alarmId, props.deviceId]),
    });

    const statusData = dataFromDataset(dataset);

    return <Context.Provider value={statusData}>{props.children}</Context.Provider>;
};

const TestAlarmStatusDataProvider: React.FC<{
    value: ContextState;
}> = (props) => <Context.Provider value={props.value}>{props.children}</Context.Provider>;

export { AlarmStatusDataProvider, TestAlarmStatusDataProvider, useAlarmStatusData };
